<template>
  <div>
    <div class="p-4 bg-secondary text-white">
      <h4 class="uppercase mb-0">{{ $t('titles.environment') }}</h4>
    </div>
    <div class="p-4">
      <!-- Environment Value Note -->
      <div class="rounded border bg-gray-100 mb-4" v-if="showEnvironmentNotes(currentScope)">
        <div class="p-4 text-sm text-gray-700">
            <p class="text-danger">{{ $t('generic.pleaseNote') }}:</p>
            <div v-html="currentScope.environment_note.body"></div>
        </div>
      </div>
      <div class="flex flex-col lg:flex-row lg:flex-wrap">
        <!-- Altitude -->
        <div class="form-group w-full lg:w-1/2 lg:pr-3">
          <label class="form-label" for="altitude">
            <span>{{ $t("altitude.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content=altitudeHelpText>
            </i>
          </label>

          <div class="input-group">
            <input
              name="turret_customization[altitude]"
              type="number"
              step="any"
              class="form-control"
              :class="{'error': errors.has('turret_customization[altitude]')}"
              :placeholder="$t('altitude.placeholder')"
              :data-vv-as="$t('altitude.label')"
              v-validate=altitudeValidation
              v-model="altitude">
            </input>
            <div class="input-group-append">
              <div class="input-group-text text-xs uppercase">{{ this.isImperial ? "FT" : "Meters" }}</div>
            </div>
          </div>
          <span class="form-hint error">{{ errors.first("turret_customization[altitude]") }}</span>
        </div>

        <!-- Temperature -->
        <div class="form-group w-full lg:w-1/2">
          <label class="form-label" for="temperature">
            <span>{{ $t("temperature.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content=temperatureHelpText>
            </i>
          </label>

          <div class="input-group">
            <input
              name="turret_customization[temperature]"
              type="number"
              step="any"
              class="form-control"
              :class="{'error': errors.has('turret_customization[temperature]')}"
              :placeholder="$t('temperature.placeholder')"
              :data-vv-as="$t('temperature.label')"
              v-validate=temperatureValidation
              v-model="temperature">
            </input>
            <div class="input-group-append">
              <div class="input-group-text text-xs uppercase">° {{ this.isImperial ? $t("generic.fahrenheit") : $t("generic.celsius") }}</div>
            </div>
          </div>
          <span class="form-hint error">{{ errors.first("turret_customization[temperature]") }}</span>
        </div>

        <!-- Pressure -->
        <div class="form-group w-full lg:w-1/2 lg:pr-3">
          <label class="form-label" for="pressure">
            <span>{{ $t("pressure.label") }}</span>
            <span class="label-hint">(16 - 40)</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content="$t('pressure.helpText')">
            </i>
          </label>

          <div class="input-group">
            <input
              name="turret_customization[pressure]"
              type="number"
              step="any"
              class="form-control"
              :class="{'error': errors.has('turret_customization[pressure]')}"
              :placeholder="$t('pressure.placeholder')"
              :data-vv-as="$t('pressure.label')"
              v-validate="'required|between:16,40'"
              v-model="pressure">
            </input>
            <div class="input-group-append">
              <div class="input-group-text text-xs">inHG</div>
            </div>
          </div>
          <span class="form-hint error">{{ errors.first("turret_customization[pressure]") }}</span>
        </div>

        <!-- Humidity -->
        <div class="form-group w-full lg:w-1/2">
          <label class="form-label" for="humidity">
            <span>{{ $t("humidity.label") }}</span>
            <span class="label-hint">(0 - 100)</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content="$t('humidity.helpText')">
            </i>
          </label>

          <div class="input-group">
            <input
              name="turret_customization[humidity]"
              type="number"
              step="any"
              class="form-control"
              :class="{'error': errors.has('turret_customization[humidity]')}"
              :placeholder="$t('humidity.placeholder')"
              :data-vv-as="$t('humidity.label')"
              v-validate="'required|between:0,100'"
              v-model="humidity">
            </input>
            <div class="input-group-append">
              <div class="input-group-text text-xxs">
                <i class="fas fa-percent"></i>
              </div>
            </div>
          </div>
          <span class="form-hint error">{{ errors.first("turret_customization[humidity]") }}</span>
        </div>
      </div>
      <div class="p-4 bg-gray-100 text-center border rounded text-sm text-gray-700 mb-4">
        <span v-html="$t('environment.formHelper')"></span>
        <a href="/frequently_asked_questions#item_2" target="_blank">{{$t('environment.formHelperLink')}}</a>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex"

const { mapGetters } = createNamespacedHelpers("turretCustomizer")

export default {
  computed: {
    ...mapGetters([
      "currentScope",
      "isImperial"
    ]),
    altitude: {
      get() { return this.$store.getters["turretCustomizer/altitude"] },
      set(newAltitude) { this.$store.dispatch("turretCustomizer/updateAltitude", newAltitude) }
    },
    pressure: {
      get() { return this.$store.getters["turretCustomizer/pressure"] },
      set(newPressure) { this.$store.dispatch("turretCustomizer/updatePressure", newPressure) }
    },
    temperature: {
      get() { return this.$store.getters["turretCustomizer/temperature"] },
      set(newTemperature) { this.$store.dispatch("turretCustomizer/updateTemperature", newTemperature) }
    },
    humidity: {
      get() { return this.$store.getters["turretCustomizer/humidity"] },
      set(newHumidity) { this.$store.dispatch("turretCustomizer/updateHumidity", newHumidity) }
    },
    temperatureHelpText() {
      return this.isImperial ? this.$t("temperature.helpTextImperial") : this.$t("temperature.helpTextMetric")
    },
    temperatureValidation() {
      return this.isImperial ? { required: true, between: [-40, 140] } : { required: true, between: [-40, 50] }
    },
    altitudeHelpText() {
      return this.isImperial ? this.$t("altitude.helpTextImperial") : this.$t("altitude.helpTextMetric")
    },
    altitudeValidation() {
      return this.isImperial ? { required: true, between: [0, 15000] } : { required: true, between: [0, 5000] }
    },
  },
  methods: {
    showEnvironmentNotes() {
      return this.currentScope.environment_note && this.currentScope.environment_note.body.length > 0;
    }
  }
}
</script>
